import { darkenHex } from '@/lib/color';
import { safeBackgroundImage, safeHref } from '@/lib/storefront/safe-url';

/** Editable page-level settings stored in `site_configs.settings`. */
export interface PageSettings {
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonicalUrl?: string;
  keywords?: string;
  noIndex?: boolean;
  noFollow?: boolean;
  backgroundType?: 'none' | 'color' | 'gradient' | 'image';
  backgroundColor?: string;
  backgroundImage?: string;
}

export interface CustomFontFace {
  family: string;
  url: string;
  format: 'woff2' | 'woff' | 'truetype' | string;
}

/** Page-level design tokens stored in `site_configs.design_tokens`. */
export interface PageDesignTokens {
  colors?: {
    primary?: string;
    secondary?: string;
    background?: string;
    text?: string;
  };
  fonts?: {
    body?: string;
    heading?: string;
  };
  customFonts?: CustomFontFace[];
  /** Sanitized page-scoped CSS. Never applied to the editor chrome. */
  customCss?: string | null;
  spacing?: Record<string, string>;
  borderRadius?: Record<string, string>;
  shadows?: Record<string, string>;
  breakpoints?: Record<string, string>;
}

/** Inline styles that paint the page background chosen in the settings panel. */
export function pageBackgroundStyle(settings: PageSettings): Record<string, string> {
  const style: Record<string, string> = {};
  if (settings.backgroundType === 'color' && settings.backgroundColor) {
    if (/^#[0-9a-fA-F]{6}$/.test(settings.backgroundColor)) {
      style.backgroundColor = settings.backgroundColor;
    }
  }
  if (settings.backgroundType === 'gradient' && settings.backgroundImage) {
    const gradient = safeBackgroundImage(settings.backgroundImage, 'gradient');
    if (gradient) style.backgroundImage = gradient;
  }
  if (settings.backgroundType === 'image' && settings.backgroundImage) {
    const image = safeBackgroundImage(settings.backgroundImage, 'image');
    if (image) {
      style.backgroundImage = image;
      style.backgroundSize = 'cover';
      style.backgroundPosition = 'center';
    }
  }
  return style;
}

/** Safe Open Graph image URL, or empty when the value is not a site/http(s) URL. */
export function safeOgImage(value: unknown): string {
  return safeHref(value, '');
}

/** CSS custom properties that override the agency theme inside one page. */
export function pageDesignCssVars(tokens: PageDesignTokens): Record<string, string> {
  const vars: Record<string, string> = {};
  const primary = tokens.colors?.primary?.trim();
  if (primary) {
    vars['--color-primary'] = primary;
    if (/^#[0-9a-fA-F]{6}$/.test(primary)) {
      vars['--color-primary-dark'] = darkenHex(primary);
    }
  }
  const secondary = tokens.colors?.secondary?.trim();
  if (secondary) vars['--color-secondary'] = secondary;
  const font = tokens.fonts?.body?.trim();
  if (font) vars['--font-sans'] = font;

  const radii = tokens.borderRadius;
  if (radii) {
    for (const key of ['md', 'lg', 'xl', '2xl', '3xl'] as const) {
      const value = radii[key];
      if (typeof value === 'string' && value) vars[`--radius-${key}`] = value;
    }
  }

  return vars;
}

/** Builds @font-face rules for uploaded page fonts. URLs stay relative to the site origin. */
export function pageFontFaceCss(tokens: PageDesignTokens): string {
  const fonts = Array.isArray(tokens.customFonts) ? tokens.customFonts : [];
  return fonts
    .filter((font) => font.family && font.url)
    .map((font) => {
      const format = font.format === 'truetype' || font.format === 'woff' || font.format === 'woff2'
        ? font.format
        : 'woff2';
      const family = font.family.replace(/['"]/g, '');
      const url = font.url.replace(/["']/g, '');
      return `@font-face{font-family:'${family}';src:url('${url}') format('${format}');font-display:swap;}`;
    })
    .join('\n');
}
